import { collectionToStories } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useDeviceType } from "../../../../components/customHooks/useDeviceType";
import ListingAdSlot from "../../../../components/molecules/ListingAdSlot";
import { getTemplateName, trackLoadMoreClick } from "../../../../components/utils";
import { getCategoryAndSubCategory, useTrackWidgetImpression } from "../../../../ga-utils";
import { ProgressiveHydration } from "../../../hydration-component";
import { generateNavigateSlug, getSlot, getTextColor, navigateTo } from "../../../utils/utils";
import { CollectionName } from "../../Atoms/CollectionName";
import { HeroImage } from "../../Atoms/HeroImage";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { StoryCard } from "../../Molecules/StoryCard";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { StateProvider } from "../../SharedContext";

import "./three-col.m.css";

const loadMore = ({ isLoading, storyItems, getMoreStories, subsequentLoadCount = 3 }, pageType) => {
  if (typeof window !== "undefined") {
    const isProfilePage = pageType === "profile-page" || pageType === "individual-page" || pageType === "company-page";
    const { category, subCategory } = getCategoryAndSubCategory(window.location, pageType);
    const templateName = isProfilePage ? "profilepage" : getTemplateName(pageType);
    trackLoadMoreClick({
      linkCategory: isProfilePage ? "fortune india coverage" : category || "",
      category: category || "companies",
      subCategory: subCategory || "NA",
      templateName: templateName,
      pageType: pageType,
    });
  }

  if (isLoading) return;
  const limit = subsequentLoadCount;
  const offset = storyItems.length;
  getMoreStories(offset, limit);
};

const ThreeColGrid = ({
  isLoading,
  collection,
  config,
  getMoreStories,
  isolatedLoadMore,
  template,
  initialLoadCount = 6,
  isLoadMoreVisible,
  sectionSwitch = false,
  page,
  isMoreVideos,
  customSlug,
  customCollectionName,
  adType,
  showAuthorContent,
  refKey = false,
  fromStory = {},
  id = "",
}) => {
  const { theme = "", border = "", subsequentLoadCount = 4, slotConfig } = config;
  const { type = "", component } = get(slotConfig, [0], {});
  const adWidgetSlot = getSlot(type, component);

  let breakPoint = 5;

  useEffect(() => {
    if (typeof window !== "undefined" && pageType === "author-page") {
      const width = window.innerWidth;
      breakPoint = width > 1159 ? 5 : 3;
    }
  }, []);

  const storyItems =
    page === "profile-page" || page === "company-page"
      ? collection?.filter((item) => item["item-type"] === "story")
      : collectionToStories(collection);

  if (!storyItems.length) return null;

  const textColor = getTextColor(theme);

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));

  // Track widget impression using the hook
  const isBrowser = typeof window !== "undefined";
  const trackingOptions = {
    sectionName: get(collection, ["name"], ""),
    pageType: pageType,
    location: isBrowser ? window.location : {},
    config: qtConfig,
    story: fromStory,
    threshold: 0.2,
  };
  const componentRef = useTrackWidgetImpression(trackingOptions);

  const isProfilePage = ["individual-page", "company-page"].includes(pageType);
  let templateName;

  if (fromStory?.["story-template"]) {
    templateName = getTemplateName(fromStory["story-template"]);
  } else if (isProfilePage) {
    templateName = "profilepage";
  } else {
    templateName = getTemplateName(pageType);
  }
  const trackEvent = (linkPosition) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        fromStory
      );
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: linkPosition,
        section_name: collection?.name ? collection.name?.toLowerCase() : "NA",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const getLoadMore = (opts) => {
    if (!isLoadMoreVisible) return null;
    const handleReadMoreClick = () => {
      trackEvent(-1);
      navigateTo(dispatch, url);
    };

    if (opts.isBtnWithArrow) {
      return (
        <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            sectionSwitch={sectionSwitch}
            qtConfig={qtConfig}
            isBtnWithArrow={opts?.isBtnWithArrow}
          />
        </div>
      );
    }

    const url = generateNavigateSlug(collection, qtConfig);
    return (
      <ProgressiveHydration>
        <div onClick={() => loadMore(opts, page)} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            sectionSwitch={sectionSwitch}
            qtConfig={qtConfig}
            template={template}
          />
        </div>
      </ProgressiveHydration>
    );
  };

  const handleStoryClick = (index) => {
    const position = index + 1;
    trackEvent(position);
  };

  const storyCard = (story, index) => {
    return (
      <>
        <div styleName={`cards ${textColor}`} className="cards" onClick={() => handleStoryClick(index)}>
          <StoryCard
            story={story}
            border={border}
            theme={theme}
            isHorizontalMobile={textColor !== "light"}
            config={config}
          >
            <HeroImage aspectRatio={[[16, 9]]} story={story} isHorizontalWithImageLast={textColor !== "light"} />
            <StorycardContent
              theme={theme}
              border={border}
              story={story}
              isHorizontalMobile
              config={config}
              headerLevel={3}
              showAuthorContent={showAuthorContent}
            />
          </StoryCard>
        </div>
      </>
    );
  };

  const renderCollectionName = () => {
    const handleCollectionNameClick = () => {
      trackEvent(-2);
    };

    if (collection?.["associated-metadata"]?.show_collection_name || page === "custom-event-page") {
      return (
        <div onClick={handleCollectionNameClick} style={{ cursor: "pointer" }}>
          <CollectionName
            collection={collection}
            headerLevel={2}
            customSlug={customSlug}
            customCollectionName={customCollectionName}
          />
        </div>
      );
    }
  };

  return (
    <div
      className={`full-width-with-padding arrow-component ${page}`}
      data-test-id="three-col-grid"
      style={{ backgroundColor: theme, color: textColor }}
      styleName={`three-col-grid-wrapper ${textColor}`}
      ref={componentRef}
      id={id}
      {...(refKey ? { ref: refKey } : {})}
    >
      <div styleName="three-col-grid">
        <div styleName="collection-name-with-read-more">
          {page !== "profile-page" || page !== "company-page" ? (
            renderCollectionName()
          ) : (
            <h2>Fortune India Coverage</h2>
          )}
          {page !== "author-page" &&
            page !== "profile-page" &&
            getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: true })}
        </div>
        {page === "author-page" ||
        page === "section-page" ||
        page === "profile-page" ||
        page === "company-page" ||
        page === "photo-listing" ||
        page === "video-listing" ? (
          <div styleName="wrapper" className="three-col-wrapper">
            {storyItems.map((story, index) => (
              <>
                <Fragment key={`story-3col1-${index}-${get(story, ["id"], "0")}`}>{storyCard(story, index)}</Fragment>
                {(index + 1) % breakPoint === 0 &&
                  (page === "author-page" ||
                    page === "section-page" ||
                    page === "photo-listing" ||
                    page === "video-listing") && (
                    <div styleName={`${page === "author-page" ? "author-page-ad" : ""}`}>
                      <ListingAdSlot
                        adType={adType || "grid_ad"}
                        index={(index + 1) / breakPoint}
                        pageType={page}
                        adStyleName="ad-slot-size-300x250"
                        adSlotConfigName="mRec_ad"
                      />
                    </div>
                  )}
              </>
            ))}
          </div>
        ) : (
          <div className="three-col-wrapper" styleName={`wrapper ${textColor}`}>
            {storyItems
              .slice(0, (textColor === "light" || isMoreVideos) && page !== "custom-event-page" ? 5 : 6)
              .map((story, index) => (
                <Fragment key={`story-3col1-${index}-${get(story, ["id"], "0")}`}>{storyCard(story, index)}</Fragment>
              ))}
            {useDeviceType() !== "mobile" && (textColor === "light" || isMoreVideos) && adWidgetSlot}
          </div>
        )}
        {getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: false })}
      </div>
    </div>
  );
};

ThreeColGrid.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    theme: PropTypes.string,
    border: PropTypes.bool,
    subsequentLoadCount: PropTypes.number,
    slotConfig: PropTypes.element,
  }),
  getMoreStories: PropTypes.func,
  sectionSwitch: PropTypes.func,
  isLoadMoreVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  isolatedLoadMore: PropTypes.bool,
  initialLoadCount: PropTypes.number,
  template: PropTypes.string,
  page: PropTypes.string,
  isMoreVideos: PropTypes.bool,
  customSlug: PropTypes.string,
  customCollectionName: PropTypes.string,
  adType: PropTypes.string,
  showAuthorContent: PropTypes.bool,
  refKey: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  fromStory: PropTypes.object,
};

ThreeColGrid.defaultProps = {
  getMoreStories: () => {},
  config: {},
  collection: {},
  isolatedLoadMore: false,
  isLoading: false,
  isMoreVideos: false,
};

export default StateProvider(ThreeColGrid);
